import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getDataFollowKeyByDot } from '../../helpers/Common';
import ThTooltip from './ThTooltip';
import Sort from './Sort';
import { getNextIndex, sort, sortGroup } from './helpers';
import { I18n } from 'react-redux-i18n';

const LARGE_MIN_WIDTH = 100;
const MEDIUM_MIN_WIDTH = 70;
const SMALL_MIN_WIDTH = 50;

export const SORT_TYPES = {
  ASC: 'asc',
  DESC: 'desc',
};

const sortStateMap = {
  [undefined]: SORT_TYPES.ASC,
  [SORT_TYPES.ASC]: SORT_TYPES.DESC,
  [SORT_TYPES.DESC]: SORT_TYPES.ASC,
};

const numberSortStateMap = {
  [undefined]: SORT_TYPES.DESC,
  [SORT_TYPES.ASC]: SORT_TYPES.DESC,
  [SORT_TYPES.DESC]: SORT_TYPES.ASC,
};

class TableHead extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isResize: false,
    };
    const { setParentFunction } = this.props;
    setParentFunction(this.calcWidths);
    this.resizeState = {};
    this.initWidths = {};
    this.widths = {};
    this.tableWidth = null;
    this.columnRefs = {};
  }

  componentWillUnmount() {
    clearTimeout(this.timeoutWidth);
  }

  getMinWidth = () => {
    const { fontSize } = this.props;
    const map = {
      Large: LARGE_MIN_WIDTH,
      Medium: MEDIUM_MIN_WIDTH,
      Small: SMALL_MIN_WIDTH,
    };

    return map[fontSize];
  };

  disableResizeColumn = () => {
    return new Promise(resolve => {
      const { setWidths, widths } = this.props;
      const initWidths = Object.keys(this.resizeState).reduce((result, key) => {
        return {
          ...result,
          [key]: widths[key],
        };
      }, {});
      setWidths(initWidths);
      setTimeout(() => {
        resolve(true);
      }, 200);
    });
  };

  canDrag = colIndex => {
    const { draggable, stickyFirstColumn } = this.props;
    if (!draggable) {
      return false;
    }
    return !(stickyFirstColumn && colIndex === 0);
  };

  calcWidths = async (removeInlineStyle = false) => {
    const { schema } = this.props;
    if (removeInlineStyle) {
      await this.disableResizeColumn();
    }
    let widths = {};
    schema.forEach(({ key }) => {
      if (!this.columnRefs[key]) {
        return;
      }
      widths[key] = this.columnRefs[key].clientWidth || this.initWidths[key];
    });

    this.initWidths = widths;
    this.changeTableStyle({ widths });
  };

  onDragStart = index => e => {
    e.dataTransfer.setData('text', 'drag column');
    const { isResize } = this.state;
    if (!this.canDrag(index) || isResize) {
      e.preventDefault();
    }
    this.dragColumnIndex = index;
  };

  onDragOver = index => e => {
    if (this.dragColumnIndex) {
      e.preventDefault();
    }
    const { schema } = this.props;
    const column = schema[index];
    const isNotDragging =
      this.dragColumnIndex === null || this.dragColumnIndex === undefined;
    if (
      isNotDragging ||
      index === this.dragColumnIndex ||
      !this.canDrag(index)
    ) {
      return;
    }
    if (!this.columnRefs[column.key]) {
      return;
    }
    const targetRect = this.columnRefs[column.key].getBoundingClientRect();
    const middle = (targetRect.right - targetRect.left) / 2;
    const hoverClientX = e.clientX - targetRect.left;
    if (this.dragColumnIndex < index && hoverClientX < middle) {
      return;
    }

    if (this.dragColumnIndex > index && hoverClientX > middle) {
      return;
    }
    this.swapColumn(index);
  };

  onDragEnd = () => {
    this.dragColumnIndex = null;
  };

  swapColumn = index => {
    const { reorder } = this.props;
    reorder(this.dragColumnIndex, index);
    this.dragColumnIndex = index;
  };

  getNewIds = (key, valueFromItem, type) => {
    const { ids, data, groups } = this.props;

    let getValueFromItem = item => getDataFollowKeyByDot(item, key);
    if (valueFromItem && typeof valueFromItem === 'function') {
      getValueFromItem = valueFromItem;
    }

    if (groups) {
      return sortGroup({
        groups,
        data,
        type,
        valueFromItem: getValueFromItem,
      });
    }

    return sort({
      ids,
      data,
      type,
      valueFromItem: getValueFromItem,
    });
  };

  changeSortState = ({ key, disableSort, valueFromItem, isString }) => e => {
    if (disableSort || e.target.contains(this.resizeEl)) {
      return;
    }
    e.preventDefault();
    const { onSort, sortState } = this.props;
    const map = isString ? sortStateMap : numberSortStateMap;
    const type = map[sortState[key]];
    onSort(
      this.getNewIds(key, valueFromItem, type),
      {
        [key]: type,
      },
      sortState[key] === undefined,
    );
  };

  onResizeStart = key => e => {
    this.setState({
      isResize: true,
    });
    window.addEventListener('mousemove', this.onResizeColumn);
    window.addEventListener('mouseup', this.onStopResizeColumn);

    this.key = key;
    this.clientX = e.clientX;
  };

  isAlMostDisableResize = () => {
    const { schema } = this.props;
    const disableResizeSchema = schema.filter(col => col.disableResize);

    return schema.length === disableResizeSchema.length + 1;
  };

  canResize = e => {
    const { isResize } = this.state;
    const minWidth = Math.min(this.getMinWidth(), this.initWidths[this.key]);
    const isLessThanMinWidth =
      this.widths[this.key] + (e.clientX - this.clientX) < minWidth;

    return !(!isResize || !this.key || isLessThanMinWidth);
  };

  onResizeColumn = e => {
    const { schema } = this.props;
    this.resizeState = {
      ...this.resizeState,
      [this.key]: true,
    };
    if (!this.canResize(e)) {
      this.setState({
        isResize: false,
      });

      return;
    }
    const minWidth = Math.min(this.getMinWidth(), this.initWidths[this.key]);
    const newWidth = Math.max(
      this.widths[this.key] + (e.clientX - this.clientX),
      minWidth,
    );
    const diff = newWidth - this.widths[this.key];
    const obj = {};
    const currentIndex = schema.findIndex(col => col.key === this.key);
    if (currentIndex === schema.length - 1) {
      if (
        this.widths[this.key] + e.clientX - this.clientX <=
        this.initWidths[this.key]
      ) {
        return;
      }
    }
    const nextIndex = getNextIndex(schema, currentIndex);
    if (nextIndex) {
      obj[schema[nextIndex].key] = this.widths[schema[nextIndex].key] - diff;
    }
    this.changeTableStyle({
      key: this.key,
      widths: {
        ...this.widths,
        [this.key]: newWidth,
        ...obj,
      },
    });
    this.clientX = e.clientX;
  };

  changeTableStyle = ({ widths }) => {
    const { setWidths } = this.props;
    setWidths(widths);
    this.widths = widths;
  };

  onStopResizeColumn = () => {
    window.removeEventListener('mousemove', this.onResizeColumn);
    window.removeEventListener('mouseup', this.onStopResizeColumn);
    this.setState({
      isResize: false,
    });
    this.key = null;
  };

  getTooltipText = title => {
    try {
      if (typeof title === 'string') {
        return title.replace(/<[\w,/]*>/g, ''); // get only text when title is dangerouslySetInnerHTML
      }

      if (Array.isArray(title.props.children)) {
        return title.props.children[0];
      }

      return I18n.t(title.props.children);
    } catch (e) {
      return '';
    }
  };

  getStyle = col => {
    const { isResize } = this.state;
    const { thValign, widths } = this.props;
    let width;
    if (col.thStyle && col.thStyle.width) {
      width = col.thStyle.width;
    }
    if (widths[col.key]) {
      width = widths[col.key];
    }
    return {
      ...col.thStyle,
      minWidth: width,
      width,
      position: 'relative',
      verticalAlign: thValign,
      cursor: isResize && this.key === col.key ? 'col-resize' : 'default',
    };
  };

  render() {
    const { schema, resizeable, tableThead, sortState } = this.props;
    return (
      <thead ref={tableThead}>
        <tr>
          {schema.map((col, colIndex) => {
            const title = col.isHtml ? col.title : I18n.t(col.title);
            const displayTitle =
              typeof title === 'string' ? (
                <div dangerouslySetInnerHTML={{ __html: title }} />
              ) : (
                title
              );
            const style = this.getStyle(col);
            const tooltip = col.thTooltip || this.getTooltipText(title);
            return (
              <th
                key={colIndex}
                ref={el => (this.columnRefs[col.key] = el)}
                style={this.getStyle(col)}
              >
                <div
                  className="h-100"
                  onClick={this.changeSortState(col)}
                  onDragStart={this.onDragStart(colIndex)}
                  onDragOver={this.onDragOver(colIndex)}
                  onDrop={e => e.preventDefault()}
                  onDragEnd={this.onDragEnd}
                  draggable={this.canDrag(colIndex)}
                >
                  {tooltip ? (
                    <ThTooltip tooltip={tooltip}>{displayTitle}</ThTooltip>
                  ) : (
                    displayTitle
                  )}

                  {!col.disableSort && sortState[col.key] && (
                    <Sort type={sortState[col.key]} />
                  )}
                </div>
                {resizeable &&
                !col.disableResize &&
                !this.isAlMostDisableResize() ? (
                  <div
                    ref={el => (this.resizeEl = el)}
                    onMouseDown={this.onResizeStart(col.key)}
                    style={{
                      height: '100%',
                      display: 'inline-block',
                      width: 8,
                      cursor: 'col-resize',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                    }}
                  />
                ) : null}
              </th>
            );
          })}
        </tr>
      </thead>
    );
  }
}

TableHead.propTypes = {
  schema: PropTypes.array.isRequired,
  reorder: PropTypes.func,
  draggable: PropTypes.bool.isRequired,
  resizeable: PropTypes.bool,
  onSort: PropTypes.func,
  ids: PropTypes.array,
  stickyFirstColumn: PropTypes.bool.isRequired,
  getDataFromRedux: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  hideColumns: PropTypes.array,
  thValign: PropTypes.string.isRequired,
  sortState: PropTypes.object.isRequired,
};

TableHead.defaultProps = {
  reorder: () => {},
  setWidth: () => {},
  resizeable: true,
  onSort: () => {},
  ids: [],
  hideColumns: [],
  data: {},
};

const mapStateToProps = (state, { getDataFromRedux }) => ({
  i18n: state.i18n,
  data: getDataFromRedux(state),
  fontSize: state.fontSize,
});

export default connect(mapStateToProps)(TableHead);
